import { useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { CustomButton } from '../Buttons';
import { callBackbonePostApi } from '../../services/backboneApiService';
import { getUser } from '../../utils/cookie';

const EXPORT_ACTION = 'getWorkoutDataExport';
const TOO_LARGE_MESSAGE_FRAGMENT = 'Export too large';
const NO_EMAIL_MESSAGE_FRAGMENT = 'no email on file';
const FORMATS = { CSV: 'csv', JSON: 'json' };
const DELIVERY_CHOICES = { DOWNLOAD: 'download', EMAIL: 'email' };
const JSON_CONTENT_TYPE = 'application/json';

const formLabelSx = {
  color: 'rgba(255,255,255,0.85)',
  '&.Mui-focused': { color: 'rgba(255,255,255,0.85)' },
};

const saveFileToDevice = (filename, contentType, content) => {
  const blobUrl = URL.createObjectURL(new Blob([content], { type: contentType }));
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
};

const openDownloadUrl = (downloadUrl) => {
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const extractErrorMessage = (error) =>
  error?.response?.data?.errorMessage || error?.message || 'Something went wrong. Please try again.';

export const WorkoutDataExportButton = ({ userId }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [format, setFormat] = useState(FORMATS.CSV);
  const [includeSets, setIncludeSets] = useState(false);
  const [deliveryChoice, setDeliveryChoice] = useState(DELIVERY_CHOICES.DOWNLOAD);
  const [isExporting, setIsExporting] = useState(false);

  const loggedInUser = getUser();
  const isOwnProfile = !!userId && !!loggedInUser?.token && loggedInUser?.userId === userId;

  if (!isOwnProfile) return null;

  const buildPayload = (deliveryMode) => ({
    action: EXPORT_ACTION,
    userId,
    includeSets,
    format,
    deliveryMode,
  });

  const saveInlineResults = (response) => {
    if (format === FORMATS.JSON) {
      const content = JSON.stringify(response?.data?.data ?? {}, null, 2);
      const memberName = loggedInUser.username ?? 'member';
      saveFileToDevice(`Black Box VR - ${memberName} - Workout Data.json`, JSON_CONTENT_TYPE, content);
      return 1;
    }

    const files = response?.data?.delivery?.files || [];
    files.forEach(({ filename, contentType, content }) => saveFileToDevice(filename, contentType, content));
    return files.length;
  };

  const downloadViaS3Links = async () => {
    const response = await callBackbonePostApi(buildPayload('s3'), '');
    const files = response?.data?.delivery?.files || [];
    files.forEach(({ downloadUrl }) => openDownloadUrl(downloadUrl));
    enqueueSnackbar('Your workout data downloads have started.', { variant: 'success' });
  };

  const runDownload = async () => {
    try {
      const response = await callBackbonePostApi(buildPayload('inline'), '');
      const fileCount = saveInlineResults(response);
      enqueueSnackbar(`Downloaded ${fileCount} workout data file${fileCount === 1 ? '' : 's'}.`, { variant: 'success' });
    } catch (error) {
      if (!extractErrorMessage(error).includes(TOO_LARGE_MESSAGE_FRAGMENT)) throw error;
      await downloadViaS3Links();
    }
  };

  const runEmail = async () => {
    const response = await callBackbonePostApi(buildPayload('email'), '');
    const toEmail = response?.data?.delivery?.toEmail;
    enqueueSnackbar(
      toEmail ? `Your workout data was emailed to ${toEmail}.` : 'Your workout data is on its way to your inbox.',
      { variant: 'success' }
    );
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      if (deliveryChoice === DELIVERY_CHOICES.EMAIL) {
        await runEmail();
      } else {
        await runDownload();
      }
      setIsDialogOpen(false);
    } catch (error) {
      const message = extractErrorMessage(error);
      enqueueSnackbar(
        message.toLowerCase().includes(NO_EMAIL_MESSAGE_FRAGMENT)
          ? 'We could not find an email address on your account. Please contact support@blackbox-vr.com.'
          : message,
        { variant: 'error' }
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" pt={10} px={2}>
      <CustomButton onClick={() => setIsDialogOpen(true)}>Export My Workout Data</CustomButton>

      <Dialog
        open={isDialogOpen}
        onClose={() => !isExporting && setIsDialogOpen(false)}
        aria-labelledby="workout-export-title"
        PaperProps={{
          sx: {
            backgroundColor: '#1b1b1b',
            color: '#fff',
            backgroundImage: 'none',
            minWidth: { xs: '90%', sm: 420 },
          },
        }}
      >
        <DialogTitle id="workout-export-title" sx={{ fontWeight: 800 }}>
          Export My Workout Data
        </DialogTitle>
        <DialogContent>
          <FormControl sx={{ mt: 1 }}>
            <FormLabel sx={formLabelSx}>Format</FormLabel>
            <RadioGroup row value={format} onChange={(event) => setFormat(event.target.value)}>
              <FormControlLabel value={FORMATS.CSV} control={<Radio />} label="CSV (spreadsheet)" />
              <FormControlLabel value={FORMATS.JSON} control={<Radio />} label="JSON (raw data)" />
            </RadioGroup>
          </FormControl>
          <Box mt={2}>
            <FormControlLabel
              control={
                <Checkbox checked={includeSets} onChange={(event) => setIncludeSets(event.target.checked)} />
              }
              label="Include set-by-set details (adds a sets CSV file)"
            />
          </Box>
          <FormControl sx={{ mt: 2 }}>
            <FormLabel sx={formLabelSx}>Delivery</FormLabel>
            <RadioGroup
              row
              value={deliveryChoice}
              onChange={(event) => setDeliveryChoice(event.target.value)}
            >
              <FormControlLabel
                value={DELIVERY_CHOICES.DOWNLOAD}
                control={<Radio />}
                label="Download now"
              />
              <FormControlLabel value={DELIVERY_CHOICES.EMAIL} control={<Radio />} label="Email me" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <CustomButton
            onClick={() => setIsDialogOpen(false)}
            disabled={isExporting}
            sx={{ background: 'transparent', border: '1px solid #1976d2' }}
          >
            Cancel
          </CustomButton>
          <CustomButton onClick={handleExport} loading={isExporting}>
            Export
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkoutDataExportButton;
